import api from "./api";
import { readCommenterToken } from "../helpers/publicVideoComments";

// The video routers are mounted at /api in Express. Development calls the
// server directly; production's /api reverse proxy strips one leading /api,
// so retaining this prefix here reaches the same Express path in both cases.
const videoApiPath = (path) => `/api${path}`;
const pathSegment = (value) => encodeURIComponent(String(value));

// Attaches the browser's commenter token when one exists, so the API can tell
// the author's own comments apart from everyone else's.
const withCommenterToken = (publicId, config = {}) => {
  const token = readCommenterToken(publicId);
  if (!token) return config;
  return {
    ...config,
    headers: { ...(config.headers || {}), "X-Commenter-Token": token },
  };
};

export const getPublicVideo = (publicId, config) =>
  api.get(videoApiPath(`/public/videos/${pathSegment(publicId)}`), config);

// Portal-side read: needs a logged-in session and also returns Internal Only
// videos, which the public endpoint deliberately never serves.
export const getInternalVideo = (publicId, config) =>
  api.get(videoApiPath(`/videos/${pathSegment(publicId)}`), config);

// Playback assets for one language: a Synthesia-signed MP4 URL plus whether
// captions exist. Fetched per load because the signed URL is time-limited.
export const getVideoPlayback = (publicId, languageCode, config) =>
  api.get(videoApiPath(`/public/videos/${pathSegment(publicId)}/play`), {
    ...config,
    params: { language: languageCode },
  });

// Same-origin caption proxy path, so <track> needs no CORS. Returned as a URL
// path rather than a response because the browser fetches it itself.
export const getVideoCaptionsPath = (publicId, languageCode) =>
  videoApiPath(`/public/videos/${pathSegment(publicId)}/captions`) +
  `?language=${encodeURIComponent(languageCode)}`;

/**
 * Fetches the proxied VTT text. The caller turns it into a blob URL: a <track>
 * pointing at the API origin would be cross-origin in development and blocked
 * by the browser, whereas a blob URL is always same-origin.
 */
export const fetchVideoCaptions = (publicId, languageCode) =>
  api.get(getVideoCaptionsPath(publicId, languageCode), {
    responseType: "text",
    transformResponse: [(data) => data],
  });

export const getVideoChapters = (publicId, languageCode, config) =>
  api.get(videoApiPath(`/public/videos/${pathSegment(publicId)}/chapters`), {
    ...config,
    params: { language: languageCode },
  });

export const getPublicVideoComments = (publicId, config) =>
  api.get(
    videoApiPath(`/public/videos/${pathSegment(publicId)}/comments`),
    withCommenterToken(publicId, config),
  );

export const submitPublicVideoComment = (publicId, payload) =>
  api.post(
    videoApiPath(`/public/videos/${pathSegment(publicId)}/comments`),
    payload,
    withCommenterToken(publicId),
  );

// Authorship is proved with the browser's commenter token, so no account needed.
export const updatePublicVideoComment = (publicId, commentId, payload) =>
  api.patch(
    videoApiPath(
      `/public/videos/${pathSegment(publicId)}/comments/${pathSegment(commentId)}`,
    ),
    payload,
    withCommenterToken(publicId),
  );

export const deletePublicVideoComment = (publicId, commentId) =>
  api.delete(
    videoApiPath(
      `/public/videos/${pathSegment(publicId)}/comments/${pathSegment(commentId)}`,
    ),
    withCommenterToken(publicId),
  );

export const getVideoLibrary = () => api.get(videoApiPath("/videoLibrary"));

export const getVideoLibraryItem = (videoId) =>
  api.get(videoApiPath(`/videoLibrary/${pathSegment(videoId)}`));

// Drives the sidebar badge: open = not deleted and not marked done.
export const getOpenVideoCommentCount = () =>
  api.get(videoApiPath("/videoLibrary/comments/open-count"));

export const createVideo = (payload) =>
  api.post(videoApiPath("/videoLibrary"), payload);

export const updateVideo = (videoId, payload) =>
  api.patch(
    videoApiPath(`/videoLibrary/${pathSegment(videoId)}`),
    payload,
  );

export const setVideoPublished = (videoId, isPublished) => {
  const action = isPublished ? "publish" : "unpublish";
  return api.post(
    videoApiPath(`/videoLibrary/${pathSegment(videoId)}/${action}`),
  );
};

export const deleteVideo = (videoId) =>
  api.delete(videoApiPath(`/videoLibrary/${pathSegment(videoId)}`));

  // Soft-deleted comments are kept for audit, so moderation can list them on their
// own and restore any of them regardless of moderation status.
  export const getVideoComments = ({ resolved, deletedOnly = false, videoId } = {}) => {
    const params = {};
    if (videoId !== undefined && videoId !== null) params.videoId = videoId;
    if (deletedOnly) {
      params.deletedOnly = 1;
    } else if (resolved !== undefined) {
      params.resolved = resolved ? 1 : 0;
    }
    return api.get(videoApiPath("/videoLibrary/comments"), { params });
  };

// "Done" replaces the old approval step: comments are public immediately and an
// admin marks one resolved once it has been dealt with.
export const setVideoCommentResolved = (commentId, resolved) =>
  api.patch(
    videoApiPath(
      `/videoLibrary/comments/${pathSegment(commentId)}/${
        resolved ? "resolve" : "reopen"
      }`,
    ),
  );

export const replyToVideoComment = (commentId, body) =>
  api.post(videoApiPath(`/videoLibrary/comments/${pathSegment(commentId)}/reply`), {
    body,
  });

export const restoreVideoComment = (commentId) =>
  api.patch(
    videoApiPath(`/videoLibrary/comments/${pathSegment(commentId)}/restore`),
  );

// Admin removal is a soft delete (DeletedBy: 'admin'), so the Deleted view can
// still restore it.
export const deleteVideoComment = (commentId) =>
  api.delete(videoApiPath(`/videoLibrary/comments/${pathSegment(commentId)}`));
